import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Main from './pages/Main';
import Taskbar from './components/Taskbar';
import rashy from './assets/rashy.jpg';
import { useState, useEffect, useRef } from 'react';
import Login from './pages/Login';
import { AnimatePresence, motion } from 'framer-motion';
import Settings from './components/Settings';
import { useAuthContext } from './hooks/useAuthContext';
import { UserSettingsProvider, useUserSettings } from './context/UserSettingsContext';
import { localStorageManager } from './utils/localStorageManager';

function AppContent() {
  const manager = new localStorageManager();
  const { user } = useAuthContext();
  const { volume, brightness, updateVolume, updateBrightness } = useUserSettings();
  const [apps, setApps] = useState(['Terminal']);
  const [openApps, setOpenApps] = useState([]);
  const [focusedAppId, setFocusedAppId] = useState(null);

  const bringToFront = (appId) =>{
    setFocusedAppId(appId);
  }

  const getAppZIndex = (appId) => {
    const baseZIndex = 1000;
    return focusedAppId === appId ? baseZIndex + 100 : baseZIndex;
  };

  const [lastKey, setLastKey] = useState(null);
  const lastKeyRef = useRef(null);
  const [isWinBarOpen, setIsWinBarOpen] = useState(false);
  const [areSettingsOpen, setAreSettingsOpen] = useState(false);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if((e.key === 'w' && lastKeyRef.current === "Shift") || (e.key === 'W' && lastKeyRef.current === "Shift")) {
        e.preventDefault();
        setIsWinBarOpen(prev => !prev);
      }

      if((e.key === 's' && lastKeyRef.current === "Shift") || (e.key === 'S' && lastKeyRef.current === "Shift")){
        e.preventDefault();
        setAreSettingsOpen(prev => !prev);
      }
      
      setLastKey(e.key);
      lastKeyRef.current = e.key;
    };

    if(user)
      document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [user]);

  const [bgImg, setBgImg] = useState(manager.getBackgroundImage());
  const [stImg, setStImg] = useState(manager.getStarterImage());

  if(!user)
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="login"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.5 }}
        >
          <Login/>
        </motion.div>
      </AnimatePresence>
    )

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="main"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <BrowserRouter>
          <div className="App w-screen h-screen bg-cover bg-center select-none overflow-hidden relative" style={{ backgroundImage: `url(${bgImg || rashy})` }}>
              <div 
                  className="absolute inset-0 bg-black transition-opacity duration-300 pointer-events-none z-[999999]"
                  style={{ opacity: Math.max(0, (100 - brightness) / 150) }}
              />
              <Taskbar 
                openApps={openApps} 
                setOpenApps={setOpenApps} 
                apps={apps} 
                bringToFront={bringToFront}
                focusedAppId={focusedAppId}
                volume={volume}
                setVolume={updateVolume}
                brightness={brightness}
                setBrightness={updateBrightness}
                isWinBarOpen={isWinBarOpen}
                setIsWinBarOpen={setIsWinBarOpen}
              />
              {areSettingsOpen && 
                <Settings 
                  volume={volume}
                  setVolume={updateVolume}
                  brightness={brightness}
                  setBrightness={updateBrightness}
                  setAreSettingsOpen={setAreSettingsOpen}
                />
              }
              <Routes> 
                <Route path = "/" element={
                  <Main 
                    apps={apps} 
                    openApps={openApps} 
                    setOpenApps={setOpenApps}
                    bringToFront={bringToFront}
                    getAppZIndex={getAppZIndex}
                    focusedAppId={focusedAppId}
                    volume={volume}
                    setVolume={updateVolume}
                  />
                }/>
              </Routes>
          </div>
        </BrowserRouter>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <UserSettingsProvider>
      <AppContent />
    </UserSettingsProvider>
  );
}

export default App;
